import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { IHTTPErrorResponse, IHTTPSuccessResponse } from './http.types'
import { Dispatch, SetStateAction } from 'react'
import config from '../../config.ts'
import { IAuthState } from '@/pages/Auth/auth.atom.ts'
import authApi from '../../pages/Auth/auth.api.ts'
import alertService from '../alerts/alert.service.ts'

const client = axios.create({
  baseURL: config.API_URL,
})

const interceptorsApplied: Record<string, boolean> = {}

export const applyInterceptors = (
  authState: IAuthState,
  setAuthState: Dispatch<SetStateAction<IAuthState>>,
  http: AxiosInstance = client,
  clientKey: string
) => {
  if (interceptorsApplied[clientKey]) return

  interceptorsApplied[clientKey] = true
  let isRefreshing = false

  let refreshRequest = Promise.resolve({
    accessToken: authState.accessToken,
    expires: authState.expires,
  })

  const ensureAuthorization = (): Promise<Pick<IAuthState, 'accessToken' | 'expires'>> => {
    const shouldRefresh = !authState || Date.now() + 600000 > authState.expires
    return shouldRefresh ? refreshToken() : Promise.resolve(authState)
  }

  const refreshToken = async (): Promise<Pick<IAuthState, 'accessToken' | 'expires'>> => {
    if (isRefreshing) return refreshRequest
    isRefreshing = true

    refreshRequest = authApi
      .refreshToken()
      .then((r) => (r.status === 'success' ? r.body.auth : { accessToken: '', expires: 0 }))
      .finally(() => (isRefreshing = false))

    return refreshRequest
  }

  http.interceptors.request.use((config) => {
    return ensureAuthorization().then(({ accessToken, expires }) => {
      authState.accessToken = accessToken
      authState.expires = expires

      config.headers.Authorization = `Bearer ${accessToken}`
      return config
    })
  })

  http.interceptors.response.use(
    (response) => response,
    (err) => {
      const shouldLogout = err.response && err.response.status === 401

      if (shouldLogout) {
        setAuthState({
          expires: 0,
          accessToken: '',
          authState: 'not-authorized',
        })
      }

      throw err
    }
  )
}

export const handleHttpResponse = <T extends any>(
  response: Pick<AxiosResponse<T>, 'data'>
): IHTTPSuccessResponse<T> => {
  return { status: 'success', body: response.data }
}

export const handleHttpError = (error: AxiosError): IHTTPErrorResponse => {
  if (error?.response?.status === 400) {
    const validationError = error?.response?.data as Record<string, string[] | string>
    if (validationError) {
      for (const key in validationError) {
        if (Array.isArray(validationError[key])) validationError[key] = validationError[key][0]
      }
    }
    return {
      status: 'error',
      message: 'Отправленны некорректные данные',
      body: error?.response?.data as Record<string, string>,
    }
  }

  if (error?.response?.status >= 500) {
    alertService.requestAlert({ type: 'error', title: `Непредвиденная ошибка`, description: `${error?.message}` })
  }

  const message = error?.message || 'Ошибка при запросе на\n' + error.config.url
  return { status: 'error', message: message }
}

const http = client
export default http
